import { DATE_GRANULARITIES, type DateGranularity } from '@ofmos/shared/record-group';
import {
  REPORT_CHART_TYPES,
  REPORT_METRIC_OPS,
  REPORT_RANGES,
  type Report,
  type ReportChartType,
  type ReportDefinition,
  type ReportMetricOp,
  type ReportRange,
  reportDefinitionSchema,
} from '@ofmos/shared/report';
import type { ViewFilterNode } from '@ofmos/shared/view';
import { useEffect, useMemo, useState } from 'react';
import { useFields, useObjects } from '../../data/hooks';
import { cx, GroupedForm, GroupedFormRow, Input, SelectMenu, SplitDivider } from '../../ui';
import { FilterBuilder } from '../filters/FilterBuilder';
import { rangeLabel } from './reportsModel';

/**
 * THE EDITOR IS AN INSPECTOR PANE (`spec/screens/reports.md` section 5, rulebook M10).
 *
 * A third column trailing of the report, toggled from the toolbar, holding the kit's
 * grouped-form rows with a pop-up button per choice, in one linear path: what it is about,
 * what it measures, how it is split, how it is drawn, over what stretch, and the filter.
 * Every change re-renders the chart beside it at once, and there is no Save button.
 *
 * IT NEEDED NO NEW PRIMITIVE, which is the decision the brief asked for. `ui/SplitDivider`
 * is the third column's divider, `ui/GroupedForm` is the group box and its rows (Parent
 * B's, imported), `ui/SelectMenu` in its `popup` variant is the macOS pop-up button the
 * settings forms already use (P7 round 2 F6), `ui/Input` is the name field, and the filter
 * is `app/filters/FilterBuilder` in the compact layout it was built for - a 20rem popover
 * panel, which is the width of this pane. Nothing here is a new part, so the built
 * editor's dialog is gone rather than rehoused. Ledger E-W4.
 *
 * WHY A DIALOG WAS THE WRONG CONTAINER, in one line, because the ledger's D-15 argued the
 * other way: the old editor answered M10 by putting a live PREVIEW inside the modal, so
 * the thing being judged was not covered - but the thing being judged is the REPORT, and a
 * preview of it beside the form is a second rendering of the same question that can
 * disagree with the page underneath. An inspector has no second copy.
 *
 * THE REPORT SAVES AS IT CHANGES, AND A CHANGE THAT CANNOT BE SAVED IS REFUSED RATHER THAN
 * APPLIED. Every control writes into a draft; a draft the shared schema accepts is written
 * straight through (a PATCH for a report that exists, a POST for one being built), and a
 * draft it refuses shows the refusal under the control that caused it and writes nothing.
 * The NAME is the one exception and commits on blur: a PATCH per keystroke is not "saves
 * as it changes", it is a write amplifier, and blur is where this product has validated an
 * in-place edit since the record page.
 */

const CHART_LABELS: Record<ReportChartType, string> = {
  bar: 'Bar',
  bar_stacked: 'Stacked bar',
  line: 'Line',
  area: 'Area',
  donut: 'Donut',
  funnel: 'Funnel',
  number: 'Big number',
};

const METRIC_LABELS: Record<ReportMetricOp, string> = {
  count: 'How many records',
  sum: 'Sum of',
  avg: 'Average of',
  min: 'Smallest',
  max: 'Largest',
};

const GRANULARITY_LABELS: Record<DateGranularity, string> = {
  DAY: 'By day',
  WEEK: 'By week',
  MONTH: 'By month',
  QUARTER: 'By quarter',
  YEAR: 'By year',
};

export interface ReportDraft {
  readonly name: string;
  readonly description: string;
  readonly definition: Partial<ReportDefinition> & { readonly objectId: string };
}

export function draftOf(report: Report | null): ReportDraft {
  return report === null
    ? {
        name: 'Untitled report',
        description: '',
        definition: { objectId: '', metric: { op: 'count' }, chart: 'bar', range: 'all_time' },
      }
    : {
        name: report.name,
        description: report.description ?? '',
        definition: report.definition,
      };
}

export interface ReportInspectorProps {
  /** The report being edited, or null while a new one is being built. */
  readonly report: Report | null;
  /**
   * In the split view it is a column; below it, the rebuilt phone sheet contains it.
   * The sheet uses the full available width and has no divider to drag.
   */
  readonly split: boolean;
  readonly width: number;
  readonly min: number;
  readonly max: number;
  readonly onWidth: (next: number) => void;
  /** A draft the schema accepts: written through at once. */
  readonly onCommit: (draft: ReportDraft, definition: ReportDefinition) => void;
  /** The name, committed on blur, for a report that already exists. */
  readonly onRename: (name: string, description: string) => void;
  /** A refusal from the server (a duplicate name, a dimension the model cannot group by). */
  readonly writeError: string | null;
}

export function ReportInspector({
  report,
  split,
  width,
  min,
  max,
  onWidth,
  onCommit,
  onRename,
  writeError,
}: ReportInspectorProps) {
  const objects = useObjects();
  const [draft, setDraft] = useState<ReportDraft>(() => draftOf(report));

  /**
   * THE DRAFT FOLLOWS THE REPORT'S IDENTITY, AND NOTHING ELSE. Selecting another row while
   * the inspector is open must not leave the previous report's answers in the controls.
   *
   * IT DELIBERATELY DOES NOT FOLLOW `updatedAt`: every write on this screen starts HERE, so
   * the server's echo of a patch can only ever agree with the draft that caused it - and a
   * reseed on that echo would overwrite whatever the person typed while the round trip was
   * in the air, which is the same class of silent loss the screen's `justCreated` closes
   * from the other side.
   */
  // biome-ignore lint/correctness/useExhaustiveDependencies: the draft follows the report's IDENTITY, not its every version
  useEffect(() => {
    if (report !== null) setDraft(draftOf(report));
  }, [report?.id]);

  const fields = useFields(draft.definition.objectId, draft.definition.objectId !== '');
  const parsed = useMemo(
    () => reportDefinitionSchema.safeParse(draft.definition),
    [draft.definition],
  );
  const issueFor = (path: string): string | undefined =>
    parsed.success
      ? undefined
      : parsed.error.issues.find((issue) => issue.path[0] === path)?.message;

  /** One write per accepted change: the draft moves, and if it parses it is the report. */
  const edit = (next: Partial<ReportDefinition>) => {
    const definition = { ...draft.definition, ...next };
    const nextDraft = { ...draft, definition };
    setDraft(nextDraft);
    const check = reportDefinitionSchema.safeParse(definition);
    if (check.success) onCommit(nextDraft, check.data);
  };

  const objectOptions = (objects.data ?? []).map((object) => ({
    value: object.id,
    label: object.labelPlural,
  }));
  const fieldOptions = (fields.data ?? []).map((field) => ({
    value: field.id,
    label: field.label,
  }));
  const metric = draft.definition.metric ?? { op: 'count' as const };

  return (
    <aside
      className={cx('reports-inspector', !split && 'reports-inspector-full')}
      aria-label="Report editor"
      {...(split ? { style: { width: `${String(width)}px` } } : {})}
    >
      {split && (
        <SplitDivider
          label="Resize the report editor"
          width={width}
          min={min}
          max={max}
          side="trailing"
          onWidth={onWidth}
          className="reports-inspector-grip"
        />
      )}
      <div className="reports-inspector-body">
        <GroupedForm title="Report" {...(writeError === null ? {} : { footer: writeError })}>
          {/*
            THE NAME IS ITS OWN ROW, NOT A TRAILING BOX (wave 4 E2 round 2, finding 1,
            cause C2-19). Beside a 120px label column the field was 135 wide and "Customers
            by lifecycle" - 174px of text, read in the running browser at 1440 with the
            inspector open - was cut to "Customers by lif" with no way to see the rest. A
            name is a free string, so it gets the row the way `Description` below it does:
            the label above, the field across the whole group, which is the kit's own answer
            for a value its trailing control cannot hold (`Forms/Example Forms/Light`, "a
            long value is served by widening the trailing control", KIT-LOOKUP section 4).
          */}
          <GroupedFormRow label="Name" control stacked>
            <Input
              value={draft.name}
              onChange={(event) => setDraft({ ...draft, name: event.target.value })}
              onBlur={() => onRename(draft.name, draft.description)}
              aria-label="The report's name"
            />
          </GroupedFormRow>
          <GroupedFormRow label="Description" control stacked>
            <Input
              value={draft.description}
              onChange={(event) => setDraft({ ...draft, description: event.target.value })}
              onBlur={() => onRename(draft.name, draft.description)}
              placeholder="What this report answers"
              aria-label="What this report answers"
            />
          </GroupedFormRow>
        </GroupedForm>

        <GroupedForm title="Question">
          <GroupedFormRow label="Object" control>
            <SelectMenu
              variant="popup"
              value={draft.definition.objectId === '' ? undefined : draft.definition.objectId}
              onValueChange={(value) =>
                /*
                  CHANGING THE OBJECT CLEARS EVERY FIELD BELOW IT, because those ids belong
                  to the old object and would be refused by the server with a message about
                  a field the reader never chose.
                */
                edit({
                  objectId: value,
                  metric: { op: 'count' },
                  groupBy: undefined,
                  segmentBy: undefined,
                  rangeFieldId: undefined,
                  filter: null,
                })
              }
              options={objectOptions}
              placeholder="Choose"
              ariaLabel="Which records this report is about"
            />
          </GroupedFormRow>

          <GroupedFormRow
            label="Measure"
            control
            {...(issueFor('metric') === undefined ? {} : { note: issueFor('metric') })}
          >
            <SelectMenu
              variant="popup"
              value={metric.op}
              onValueChange={(value) =>
                edit({
                  metric:
                    value === 'count'
                      ? { op: 'count' }
                      : { op: value as ReportMetricOp, fieldId: metric.fieldId },
                })
              }
              options={REPORT_METRIC_OPS.map((op) => ({ value: op, label: METRIC_LABELS[op] }))}
              ariaLabel="The number this report shows"
            />
          </GroupedFormRow>
          {metric.op !== 'count' && (
            <GroupedFormRow label="Field" control>
              <SelectMenu
                variant="popup"
                value={metric.fieldId}
                onValueChange={(value) => edit({ metric: { op: metric.op, fieldId: value } })}
                options={fieldOptions}
                placeholder="Choose"
                ariaLabel="Which field to measure"
              />
            </GroupedFormRow>
          )}

          <GroupedFormRow
            label="Split by"
            control
            {...(issueFor('groupBy') === undefined ? {} : { note: issueFor('groupBy') })}
          >
            <SelectMenu
              variant="popup"
              value={draft.definition.groupBy?.fieldId}
              onValueChange={(value) => edit({ groupBy: { fieldId: value } })}
              options={fieldOptions}
              placeholder="Choose"
              ariaLabel="What the chart is split by"
            />
          </GroupedFormRow>
          <GroupedFormRow label="Bucket" control>
            <SelectMenu
              variant="popup"
              value={draft.definition.groupBy?.granularity}
              onValueChange={(value) =>
                edit({
                  groupBy: {
                    fieldId: draft.definition.groupBy?.fieldId ?? '',
                    granularity: value as DateGranularity,
                  },
                })
              }
              options={DATE_GRANULARITIES.map((granularity) => ({
                value: granularity,
                label: GRANULARITY_LABELS[granularity],
              }))}
              placeholder="Not a date"
              ariaLabel="How dates are bucketed"
            />
          </GroupedFormRow>

          <GroupedFormRow
            label="Segment"
            control
            {...(issueFor('segmentBy') === undefined ? {} : { note: issueFor('segmentBy') })}
          >
            <SelectMenu
              variant="popup"
              value={draft.definition.segmentBy?.fieldId}
              onValueChange={(value) => edit({ segmentBy: { fieldId: value } })}
              options={fieldOptions}
              placeholder="No second split"
              ariaLabel="A second split, for bars and lines"
            />
          </GroupedFormRow>

          <GroupedFormRow label="Chart" control>
            <SelectMenu
              variant="popup"
              value={draft.definition.chart}
              onValueChange={(value) => edit({ chart: value as ReportChartType })}
              options={REPORT_CHART_TYPES.map((chart) => ({
                value: chart,
                label: CHART_LABELS[chart],
              }))}
              ariaLabel="Which chart to draw"
            />
          </GroupedFormRow>

          <GroupedFormRow
            label="Range"
            control
            {...(issueFor('rangeFieldId') === undefined ? {} : { note: issueFor('rangeFieldId') })}
          >
            <SelectMenu
              variant="popup"
              value={draft.definition.range}
              onValueChange={(value) => edit({ range: value as ReportRange })}
              options={REPORT_RANGES.map((range) => ({
                value: range,
                label: rangeLabel(range),
              }))}
              ariaLabel="The stretch of time"
            />
          </GroupedFormRow>
          <GroupedFormRow label="Date" control>
            <SelectMenu
              variant="popup"
              value={draft.definition.rangeFieldId}
              onValueChange={(value) => edit({ rangeFieldId: value })}
              options={fieldOptions}
              placeholder="The date it is split by"
              ariaLabel="Which date the range applies to"
            />
          </GroupedFormRow>
        </GroupedForm>

        {draft.definition.objectId !== '' && fields.data !== undefined && (
          <GroupedForm
            title="Filter"
            footer="Apply writes the filter into the report; the chart redraws with it."
          >
            <div className="reports-inspector-filter">
              <FilterBuilder
                objectId={draft.definition.objectId}
                fields={fields.data}
                value={(draft.definition.filter ?? null) as ViewFilterNode | null}
                onChange={(filter) => edit({ filter })}
                onDone={() => undefined}
              />
            </div>
          </GroupedForm>
        )}
      </div>
    </aside>
  );
}
